/**
 * Settings (prototype VIEWS.settings) — step 1 ships two tabs:
 *  - People: everyone with access, their role and status. Invite (POST /users), change role inline and
 *    edit or disable in a side panel (PATCH /users/{id} with If-Match). Disabling ends access at once.
 *  - SPOCs: who looks after each client, and "Change SPOC" with a reason (POST /clients/{id}/spoc).
 *  - Security → Acting-as log (ADR-006, `audit.read` only): every acting / viewing session, filterable.
 *  - Scores and flags (step 6, `settings.write`): health thresholds and penalties, the other thresholds, and the
 *    twelve flag rules (GET/PATCH /settings/thresholds and /settings/flags).
 *  - Engagement (step 6, `settings.write`): the eight engagement activities and their cadences.
 *  - Products and usage (step 5, `settings.write`): products, accepted report headers, what each client bought and
 *    where each client's numbers come from (ProductsUsage).
 *  - Recordings and privacy (step 8, `settings.write`): consent before audio upload, audio retention (founder only),
 *    the consent text, the tone label for members (RecordingsPrivacy).
 *  - My connections (step 8): my Gmail and Calendar, and for managers who has connected (Connections).
 *  - System (ADR-004 §7, `settings.system` only): the AI provider — keys, tests, the per-purpose matrix, usage
 *    (SystemAi). Kept apart from the business tabs; anyone else gets no tab and a not-found page at its address.
 *  - Voice and approval (`settings.write`): auto-approve confidence and the time to check (VoiceApproval).
 *  - Reminders (`settings.write`): how v1 reaches people; in-app only, email and push deferred (Reminders).
 * The prototype's six tabs come first, in its order, on one row that scrolls sideways when it is too wide.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { ROLES, ROLE_LABEL, type Capability, type ClientRow, type Role, type User } from '@amplispot/contracts';
import { ApiFailure } from '../../api';
import { useSession } from '../../lib/session';
import { useClients, useUpdateUser, useUsers } from '../../lib/data';
import { Avatar, Empty, ErrorState, HealthChip, Loading, Pills, PriorityChip, errorText, useToast } from '../../ui/kit';
import { PAGE_SUB } from '../../shell/copy';
import { PageInfo } from '../../shell/Help';
import { ChangeSpocModal, type SpocTarget } from '../ChangeSpocModal';
import { InviteModal } from './InviteModal';
import { UserDrawer } from './UserDrawer';
import { ActAsLog } from './ActAsLog';
import { Thresholds } from './Thresholds';
import { EngagementCadences } from './EngagementCadences';
import { ProductsUsage } from './ProductsUsage';
import { RecordingsPrivacy } from './RecordingsPrivacy';
import { Connections } from './Connections';
import { SystemAi } from './SystemAi';
import { Integrations } from './Integrations';
import { VoiceApproval } from './VoiceApproval';
import { Reminders } from './Reminders';
import { resolveSettingsTab, visibleSettingsTabs, type SettingsTab } from './tabs';

type Tab = SettingsTab;

export function Settings() {
  const { tab } = useParams();
  const { can } = useSession();
  const tabs = visibleSettingsTabs(can);
  const at = resolveSettingsTab(tab, can);
  // Prototype ST.tab='voice': open on the first tab this person has (Voice and approval for the managers).
  if (at === 'redirect') return <Navigate to={`/settings/${tabs[0]?.[0] ?? 'people'}`} replace />;
  if (at === 'not_found')
    return (
      <div className="panel" style={{ marginTop: 16 }}>
        <Empty title="Page not found">This page does not exist, or is not one you can open.</Empty>
      </div>
    );
  return (
    <>
      <div className="ph">
        <div>
          <h1>
            Settings
            <PageInfo helpKey="settings" />
          </h1>
          <p>{PAGE_SUB.settings}</p>
        </div>
      </div>
      <OneRowTabs selected={tab}>
        {tabs.map(([k, label]) => (
          <Link key={k} to={`/settings/${k}`} className={k === tab ? 'on' : ''} role="tab" aria-selected={k === tab}>
            {label}
          </Link>
        ))}
      </OneRowTabs>
      {(tab as Tab) === 'people' ? (
        <People />
      ) : (tab as Tab) === 'voice' ? (
        <VoiceApproval />
      ) : (tab as Tab) === 'reminders' ? (
        <Reminders />
      ) : (tab as Tab) === 'security' ? (
        <ActAsLog />
      ) : (tab as Tab) === 'scores' ? (
        <Thresholds />
      ) : (tab as Tab) === 'engagement' ? (
        <EngagementCadences />
      ) : (tab as Tab) === 'products' ? (
        <ProductsUsage />
      ) : (tab as Tab) === 'integrations' ? (
        <Integrations />
      ) : (tab as Tab) === 'privacy' ? (
        <RecordingsPrivacy />
      ) : (tab as Tab) === 'connections' ? (
        <Connections />
      ) : (tab as Tab) === 'system' ? (
        <SystemAi />
      ) : (
        <SpocAssignments />
      )}
    </>
  );
}

/**
 * The Settings tab strip: one row that scrolls sideways when the person has more tabs than fit (the founder has
 * twelve; at 1440 the last few do not fit). Every tab stays reachable and you can tell more are there: a thin
 * visible scrollbar, a fade on each edge that has tabs past it, a vertical wheel scrolls the row, and the selected
 * tab (a click or a deep link such as /settings/system) is scrolled into view.
 */
function OneRowTabs({ selected, children }: { selected: string | undefined; children: ReactNode }) {
  const ref = useRef<HTMLDivElement>(null);
  const [edges, setEdges] = useState({ left: false, right: false });

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const left = el.scrollLeft > 1;
      const right = el.scrollLeft + el.clientWidth < el.scrollWidth - 1;
      setEdges((e) => (e.left === left && e.right === right ? e : { left, right }));
    };
    // A mouse wheel only moves up and down; on this row that means sideways, until the row reaches its end.
    const onWheel = (ev: WheelEvent) => {
      if (ev.deltaY === 0 || Math.abs(ev.deltaX) > Math.abs(ev.deltaY) || el.scrollWidth <= el.clientWidth) return;
      const max = el.scrollWidth - el.clientWidth;
      if ((ev.deltaY < 0 && el.scrollLeft <= 0) || (ev.deltaY > 0 && el.scrollLeft >= max)) return;
      ev.preventDefault();
      el.scrollLeft += ev.deltaY;
    };
    measure();
    el.addEventListener('scroll', measure, { passive: true });
    el.addEventListener('wheel', onWheel, { passive: false });
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', measure);
      el.removeEventListener('wheel', onWheel);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    // Only the strip scrolls (block: 'nearest' leaves the page where it is).
    ref.current?.querySelector<HTMLElement>('[aria-selected="true"]')?.scrollIntoView({ inline: 'nearest', block: 'nearest' });
  }, [selected]);

  return (
    <div
      ref={ref}
      className={`tabs one-row${edges.left ? ' more-l' : ''}${edges.right ? ' more-r' : ''}`}
      role="tablist"
    >
      {children}
    </div>
  );
}

// ---------------------------------------------------------------- People

type StatusFilter = 'active' | 'disabled' | 'all';

function People() {
  const { me, can } = useSession();
  const users = useUsers(can('users.read'));
  const update = useUpdateUser();
  const toast = useToast();
  const [status, setStatus] = useState<StatusFilter>('active');
  const [inviting, setInviting] = useState(false);
  const [editing, setEditing] = useState<string | null>(null);
  const writable = can('users.write');

  const all = users.data ?? [];
  const rows = useMemo(
    // As the prototype lists them: by role (founders first, then ops, then the requesting teams), then name.
    () =>
      all
        .filter((u) => status === 'all' || u.status === status)
        .sort((a, b) => ROLES.indexOf(a.role) - ROLES.indexOf(b.role) || a.name.localeCompare(b.name)),
    [all, status],
  );
  const n = (s: StatusFilter) => all.filter((u) => s === 'all' || u.status === s).length;

  const setRole = (u: User, role: Role) => {
    if (role === u.role) return;
    update.mutate(
      { id: u.id, version: u.version, body: { role } },
      {
        onSuccess: () => toast(`${u.name} is now ${ROLE_LABEL[role]}${u.id === me.user.id ? '. Your menu has been updated.' : ''}`),
        onError: (e) =>
          toast(
            e instanceof ApiFailure && e.status === 412
              ? `Someone else changed ${u.name} a moment ago. The list now shows their change. Make yours again.`
              : errorText(e),
          ),
      },
    );
  };

  const editingUser = editing ? all.find((u) => u.id === editing) ?? null : null;

  return (
    <>
      <div className="bar">
        <span className="lbl">Show</span>
        <Pills
          options={[
            ['active', 'Active', n('active')],
            ['disabled', 'Disabled', n('disabled')],
            ['all', 'Everyone', n('all')],
          ]}
          value={status}
          onChange={setStatus}
        />
      </div>
      <div className="panel">
        <div className="hd">
          <h3>People</h3>
          <div className="r">
            {writable ? (
              <button className="btn sm pri" onClick={() => setInviting(true)}>
                Invite someone
              </button>
            ) : null}
          </div>
        </div>
        {users.isPending ? (
          <Loading label="Loading people…" />
        ) : users.isError ? (
          <ErrorState error={users.error} onRetry={() => users.refetch()} />
        ) : rows.length === 0 ? (
          <Empty title={status === 'disabled' ? 'No one is disabled' : 'No one here yet'}>
            {writable && status !== 'disabled' ? 'Press Invite someone to add the first person.' : null}
          </Empty>
        ) : (
          rows.map((u) => (
            <div className="li person" key={u.id}>
              <Avatar user={u} />
              <div className="b">
                <div className="t" style={u.status === 'disabled' ? { color: 'var(--mute)' } : undefined}>
                  {u.name}
                  {u.id === me.user.id ? <span className="sub" style={{ fontWeight: 500, marginLeft: 6 }}>(you)</span> : null}
                </div>
                <div className="m">
                  <span>{u.email}</span>
                  {u.client_count > 0 ? <span>SPOC for {u.client_count} client{u.client_count === 1 ? '' : 's'}</span> : null}
                  {u.granted_capabilities.includes('holidays.manage') ? <span className="chip info">HR admin</span> : null}
                  {u.status === 'disabled' ? <span className="chip cold">Disabled</span> : null}
                </div>
              </div>
              {writable ? (
                <div className="ctl">
                  <select
                    className="in"
                    style={{ width: 'auto' }}
                    aria-label={`Role for ${u.name}`}
                    value={u.role}
                    disabled={u.status === 'disabled' || (update.isPending && update.variables?.id === u.id)}
                    onChange={(e) => setRole(u, e.target.value as Role)}
                  >
                    {ROLES.map((r) => (
                      <option key={r} value={r}>
                        {ROLE_LABEL[r]}
                      </option>
                    ))}
                  </select>
                  <button className="btn sm" onClick={() => setEditing(u.id)}>
                    Edit
                  </button>
                </div>
              ) : (
                <span className="chip">{ROLE_LABEL[u.role]}</span>
              )}
            </div>
          ))
        )}
      </div>
      <div className="note" style={{ marginTop: 12 }}>
        The role decides the menu, the data and the buttons. A changed role or a disabled person takes effect on their next click. Revenue is shown only to roles that may see it.
      </div>
      {inviting ? <InviteModal onClose={() => setInviting(false)} /> : null}
      {editingUser ? <UserDrawer user={editingUser} onClose={() => setEditing(null)} /> : null}
    </>
  );
}

// ---------------------------------------------------------------- SPOCs

function SpocAssignments() {
  const { can } = useSession();
  const clients = useClients();
  const [changing, setChanging] = useState<SpocTarget | null>(null);
  const [q, setQ] = useState('');
  const canChange = can('client.changeSpoc');

  const rows = useMemo(() => {
    const term = q.trim().toLowerCase();
    return (clients.data ?? []).filter(
      (c) => !term || c.name.toLowerCase().includes(term) || c.spoc.name.toLowerCase().includes(term),
    );
  }, [clients.data, q]);

  return (
    <>
      <div className="bar">
        <input className="in" style={{ maxWidth: 320 }} placeholder="Find a client or SPOC" value={q} onChange={(e) => setQ(e.target.value)} aria-label="Find a client or SPOC" />
      </div>
      <div className="panel">
        <div className="hd">
          <h3>Who looks after each client</h3>
          <div className="r">{canChange ? 'Changes need a reason and are kept in the client’s history' : 'You can see who looks after each client'}</div>
        </div>
        {clients.isPending ? (
          <Loading label="Loading clients…" />
        ) : clients.isError ? (
          <ErrorState error={clients.error} onRetry={() => clients.refetch()} />
        ) : rows.length === 0 ? (
          <Empty title={q ? 'No client or SPOC matches' : 'No clients yet'}>{q ? 'Try another name.' : 'Clients appear here once they are added.'}</Empty>
        ) : (
          <table>
            <thead>
              <tr>
                <th>Client</th>
                <th>Priority</th>
                <th>SPOC</th>
                <th>Health</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((c: ClientRow) => (
                <tr key={c.id}>
                  <td>
                    <b>{c.name}</b>
                    <div className="sub">{c.segment}</div>
                  </td>
                  <td>
                    <PriorityChip p={c.priority} />
                  </td>
                  <td>
                    <div className="split">
                      <Avatar user={c.spoc} size={26} />
                      {c.spoc.name}
                    </div>
                  </td>
                  <td>
                    <HealthChip band={c.band} score={c.health?.value ?? null} />
                  </td>
                  <td className="tr">
                    {canChange ? (
                      <button className="btn sm" onClick={() => setChanging({ id: c.id, name: c.name, spoc: c.spoc })}>
                        Change SPOC
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      {changing ? <ChangeSpocModal client={changing} onClose={() => setChanging(null)} /> : null}
    </>
  );
}
